/**
 * communityApi — typed client for the Volunteer Community backend APIs
 * (`/api/community/**`). No financial data is exchanged anywhere here.
 *
 * Mirrors the codebase service convention: a default-exported object of async
 * functions, each unwrapping the `ApiResponse<T>` envelope (`response.data.data`).
 * Functions are ALSO named-exported individually (per project GOTCHA that service
 * admin methods must be named exports) so importers can use either style.
 */
import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

// ──────────────────────────────────────────────────────────────────────────
// Response types (mirror com.tbmuktbharat.community.dto.CommunityDtos)
// ──────────────────────────────────────────────────────────────────────────
export interface UserSummary {
  id: number
  username: string
  firstName: string
  lastName: string
  fullName: string
  profileImage: string | null
}

export type CommunityRole = 'MEMBER' | 'VOLUNTEER' | 'COORDINATOR' | 'LEADER' | 'MODERATOR'

export interface CommunityResponse {
  id: number
  name: string
  slug: string
  logoUrl: string | null
  description: string
  location: string
  languages: string[]
  focusAreas: string[]
  guidelines: string
  leader: UserSummary
  coordinator: UserSummary | null
  contactEmail: string
  contactPhone: string
  status: string
  memberCount: number
  createdAt: string
  updatedAt: string
  myRole: CommunityRole | null
  isLeader: boolean
  canManage: boolean
}

export interface MemberResponse {
  user: UserSummary
  role: CommunityRole
  skills: string[]
  interests: string[]
  location: string | null
  languages: string[]
  availability: string | null
  volunteerExperience: string | null
  preferredActivityType: string | null
  joinedAt: string
  badgeCount: number
  activityCount: number
}

export interface OverviewStats {
  activeMembers: number
  campaigns: number
  awarenessActivities: number
  events: number
  resourcesShared: number
  volunteerHours: number
  volunteerHoursDataRequired: boolean
  communitiesReached: number
  communitiesReachedDataRequired: boolean
}

/** Per-volunteer activity summary (dashboard §2). volunteerHours is data-required. */
export interface MyStats {
  campaignsJoined: number
  volunteerHours: number
  volunteerHoursDataRequired: boolean
  awarenessActivities: number
  learningModulesCompleted: number
  resourcesShared: number
  communityActivities: number
}

/** Lightweight community card for discovery (§9).
 *  NOTE: the backend boolean field `isMember` serializes to JSON key `member`
 *  (Jackson strips the `is` prefix on Lombok boolean getters), so the wire
 *  field is `member`. */
export interface CommunitySummary {
  id: number
  name: string
  slug: string
  logoUrl: string | null
  description: string
  location: string
  languages: string[]
  focusAreas: string[]
  memberCount: number
  member: boolean
}

export interface ActivityResponse {
  id: number
  type: string
  entityId: number | null
  description: string
  user: UserSummary | null
  createdAt: string
}

export interface EventResponse {
  id: number
  eventUuid: string
  title: string
  description: string
  type: string
  startDate: string
  endDate: string
  location: string
  organizer: UserSummary
  capacity: number
  registeredCount: number
  status: string
  bannerUrl: string | null
  myRegistered: boolean
  skills?: string[]
}

export interface AnnouncementResponse {
  id: number
  title: string
  body: string
  category: string
  pinned: boolean
  createdBy: UserSummary
  createdAt: string
  updatedAt?: string
}

export interface BadgeResponse {
  id: number
  key: string
  name: string
  description: string
  icon: string
  criteria: string
  tier: string
  earned: boolean
}

export interface MemberBadgeResponse {
  id: number
  badge: BadgeResponse
  user: UserSummary
  awardedAt: string
  auto: boolean
}

export interface ModuleResponse {
  id: number
  key: string
  title: string
  description: string
  category: string
  orderIndex: number
  articleSlug: string | null
  durationMins: number
  icon: string
  myStatus: string
  myProgress: number
}

export interface ProgressResponse {
  moduleId: number
  status: string
  progressPct: number
  completedAt: string | null
}

export interface CommunityCampaignResponse {
  id: number
  title: string
  slug: string
  type?: string
  objective?: string
  startDate?: string
  endDate?: string
  location?: string
  status: string
  organizerName: string | null
  volunteersNeeded: number | null
  participantsCount: number
  shareCount: number
  myParticipation: boolean
  communityId: number | null
}

export interface ReportResponse {
  id: number
  postId: number | null
  commentId: number | null
  reelId: number | null
  communityId: number | null
  reporter: UserSummary
  reason: string
  details: string
  status: string
  resolvedBy: UserSummary | null
  resolutionNote: string | null
  createdAt: string
}

// ──────────────────────────────────────────────────────────────────────────
// Request types (mirror CommunityRequests)
// ──────────────────────────────────────────────────────────────────────────
export interface CommunityUpdateRequest {
  name?: string
  logoUrl?: string
  description?: string
  location?: string
  languages?: string[]
  focusAreas?: string[]
  guidelines?: string
  contactEmail?: string
  contactPhone?: string
}
export interface MemberProfileRequest {
  skills?: string[]; interests?: string[]
  location?: string; languages?: string[]; availability?: string
  volunteerExperience?: string; preferredActivityType?: string
}
export interface RoleUpdateRequest { role: string }
export interface EventCreateRequest {
  title: string; description?: string; type: string
  startDate?: string; endDate?: string; location?: string
  capacity?: number; bannerUrl?: string; status?: string; skills?: string[]
}
export interface AnnouncementRequest { title: string; body: string; category?: string; pinned?: boolean }
export interface AwardBadgeRequest { badgeId: number; userId: number }
export interface ProgressUpdateRequest { status: string; progressPct?: number }
export interface ReportRequest { postId?: number; commentId?: number; reelId?: number; communityId?: number; reason: string; details?: string }
export interface ReportResolveRequest { status: string; resolutionNote?: string }

// ──────────────────────────────────────────────────────────────────────────
// Service functions
// ──────────────────────────────────────────────────────────────────────────

// Community
export async function getMyCommunity(): Promise<CommunityResponse> {
  const r = await api.get<ApiResponse<CommunityResponse>>('/community/my')
  return r.data.data
}
export async function getCommunityById(id: number): Promise<CommunityResponse> {
  const r = await api.get<ApiResponse<CommunityResponse>>(`/community/${id}`)
  return r.data.data
}
export async function updateCommunity(id: number, data: CommunityUpdateRequest): Promise<CommunityResponse> {
  const r = await api.put<ApiResponse<CommunityResponse>>(`/community/${id}`, data)
  return r.data.data
}
export async function getOverview(communityId: number): Promise<OverviewStats> {
  const r = await api.get<ApiResponse<OverviewStats>>(`/community/${communityId}/overview`)
  return r.data.data
}
export async function getMembers(communityId: number): Promise<MemberResponse[]> {
  const r = await api.get<ApiResponse<MemberResponse[]>>(`/community/${communityId}/members`)
  return r.data.data
}
export async function setMemberRole(communityId: number, userId: number, role: string): Promise<void> {
  await api.put<ApiResponse<void>>(`/community/${communityId}/members/${userId}/role`, { role })
}
export async function updateMyMemberProfile(data: MemberProfileRequest): Promise<MemberResponse> {
  const r = await api.put<ApiResponse<MemberResponse>>('/community/me/member-profile', data)
  return r.data.data
}
export async function getMyStats(): Promise<MyStats> {
  const r = await api.get<ApiResponse<MyStats>>('/community/me/stats')
  return r.data.data
}
export async function listCommunities(): Promise<CommunitySummary[]> {
  const r = await api.get<ApiResponse<CommunitySummary[]>>('/community')
  return r.data.data
}
export async function joinCommunity(id: number): Promise<CommunityResponse> {
  const r = await api.post<ApiResponse<CommunityResponse>>(`/community/${id}/join`)
  return r.data.data
}
export async function getActivity(communityId: number, page = 0, size = 20): Promise<PaginatedResponse<ActivityResponse>> {
  const r = await api.get<ApiResponse<PaginatedResponse<ActivityResponse>>>(
    `/community/${communityId}/activity?page=${page}&size=${size}`)
  return r.data.data
}

// Events
export async function listEvents(communityId: number, status?: string, page = 0, size = 10): Promise<PaginatedResponse<EventResponse>> {
  const p = new URLSearchParams({ communityId: String(communityId), page: String(page), size: String(size) })
  if (status) p.append('status', status)
  const r = await api.get<ApiResponse<PaginatedResponse<EventResponse>>>(`/community/events?${p}`)
  return r.data.data
}
export async function getEvent(id: number): Promise<EventResponse> {
  const r = await api.get<ApiResponse<EventResponse>>(`/community/events/${id}`)
  return r.data.data
}
export async function createEvent(data: EventCreateRequest): Promise<EventResponse> {
  const r = await api.post<ApiResponse<EventResponse>>('/community/events', data)
  return r.data.data
}
export async function updateEvent(id: number, data: EventCreateRequest): Promise<EventResponse> {
  const r = await api.put<ApiResponse<EventResponse>>(`/community/events/${id}`, data)
  return r.data.data
}
export async function deleteEvent(id: number): Promise<void> {
  await api.delete<ApiResponse<void>>(`/community/events/${id}`)
}
export async function registerForEvent(id: number): Promise<EventResponse> {
  const r = await api.post<ApiResponse<EventResponse>>(`/community/events/${id}/register`)
  return r.data.data
}
export async function cancelEventRegistration(id: number): Promise<EventResponse> {
  const r = await api.delete<ApiResponse<EventResponse>>(`/community/events/${id}/register`)
  return r.data.data
}
export async function myEvents(): Promise<EventResponse[]> {
  const r = await api.get<ApiResponse<EventResponse[]>>('/community/events/my')
  return r.data.data
}

// Announcements
export async function listAnnouncements(communityId: number, page = 0, size = 10): Promise<PaginatedResponse<AnnouncementResponse>> {
  const r = await api.get<ApiResponse<PaginatedResponse<AnnouncementResponse>>>(
    `/community/announcements?communityId=${communityId}&page=${page}&size=${size}`)
  return r.data.data
}
export async function pinnedAnnouncements(communityId: number): Promise<AnnouncementResponse[]> {
  const r = await api.get<ApiResponse<AnnouncementResponse[]>>(`/community/announcements/pinned?communityId=${communityId}`)
  return r.data.data
}
export async function createAnnouncement(data: AnnouncementRequest): Promise<AnnouncementResponse> {
  const r = await api.post<ApiResponse<AnnouncementResponse>>('/community/announcements', data)
  return r.data.data
}
export async function updateAnnouncement(id: number, data: AnnouncementRequest): Promise<AnnouncementResponse> {
  const r = await api.put<ApiResponse<AnnouncementResponse>>(`/community/announcements/${id}`, data)
  return r.data.data
}
export async function toggleAnnouncementPin(id: number): Promise<AnnouncementResponse> {
  const r = await api.put<ApiResponse<AnnouncementResponse>>(`/community/announcements/${id}/pin`)
  return r.data.data
}
export async function deleteAnnouncement(id: number): Promise<void> {
  await api.delete<ApiResponse<void>>(`/community/announcements/${id}`)
}

// Recognition
export async function listBadges(): Promise<BadgeResponse[]> {
  const r = await api.get<ApiResponse<BadgeResponse[]>>('/community/recognition/badges')
  return r.data.data
}
export async function myBadges(): Promise<MemberBadgeResponse[]> {
  const r = await api.get<ApiResponse<MemberBadgeResponse[]>>('/community/recognition/my')
  return r.data.data
}
export async function communityBadges(communityId: number): Promise<MemberBadgeResponse[]> {
  const r = await api.get<ApiResponse<MemberBadgeResponse[]>>(`/community/recognition/community?communityId=${communityId}`)
  return r.data.data
}
export async function awardBadge(data: AwardBadgeRequest): Promise<MemberBadgeResponse> {
  const r = await api.post<ApiResponse<MemberBadgeResponse>>('/community/recognition/award', data)
  return r.data.data
}

// Learning
export async function listModules(): Promise<ModuleResponse[]> {
  const r = await api.get<ApiResponse<ModuleResponse[]>>('/community/learning/modules')
  return r.data.data
}
export async function updateModuleProgress(moduleId: number, data: ProgressUpdateRequest): Promise<ProgressResponse> {
  const r = await api.post<ApiResponse<ProgressResponse>>(`/community/learning/modules/${moduleId}/progress`, data)
  return r.data.data
}
export async function collectiveLearning(communityId: number): Promise<Record<string, number>> {
  const r = await api.get<ApiResponse<Record<string, number>>>(`/community/learning/collective?communityId=${communityId}`)
  return r.data.data
}

// Campaigns (community-scoped, non-financial)
export async function listCommunityCampaigns(communityId: number): Promise<CommunityCampaignResponse[]> {
  const r = await api.get<ApiResponse<CommunityCampaignResponse[]>>(`/community/campaigns?communityId=${communityId}`)
  return r.data.data
}
export async function getMyCampaigns(): Promise<CommunityCampaignResponse[]> {
  const r = await api.get<ApiResponse<CommunityCampaignResponse[]>>('/community/campaigns/my')
  return r.data.data
}
export async function getCommunityCampaign(id: number): Promise<CommunityCampaignResponse> {
  const r = await api.get<ApiResponse<CommunityCampaignResponse>>(`/community/campaigns/${id}`)
  return r.data.data
}
export async function joinCommunityCampaign(id: number): Promise<CommunityCampaignResponse> {
  const r = await api.post<ApiResponse<CommunityCampaignResponse>>(`/community/campaigns/${id}/join`)
  return r.data.data
}
export async function shareCommunityCampaign(id: number): Promise<CommunityCampaignResponse> {
  const r = await api.post<ApiResponse<CommunityCampaignResponse>>(`/community/campaigns/${id}/share`)
  return r.data.data
}
export async function inviteToCommunityCampaign(id: number, userId: number): Promise<string> {
  const r = await api.post<ApiResponse<string>>(`/community/campaigns/${id}/invite?userId=${userId}`)
  return r.data.data
}

// Reports / moderation
export async function createReport(data: ReportRequest): Promise<ReportResponse> {
  const r = await api.post<ApiResponse<ReportResponse>>('/community/reports', data)
  return r.data.data
}
export async function listReports(communityId: number, status?: string, page = 0, size = 20): Promise<PaginatedResponse<ReportResponse>> {
  const p = new URLSearchParams({ communityId: String(communityId), page: String(page), size: String(size) })
  if (status) p.append('status', status)
  const r = await api.get<ApiResponse<PaginatedResponse<ReportResponse>>>(`/community/reports?${p}`)
  return r.data.data
}
export async function resolveReport(id: number, data: ReportResolveRequest): Promise<ReportResponse> {
  const r = await api.put<ApiResponse<ReportResponse>>(`/community/reports/${id}/resolve`, data)
  return r.data.data
}

// Default aggregate (codebase convention) — re-exports the named functions above.
const communityApi = {
  getMyCommunity, getCommunityById, updateCommunity, getOverview, getMembers,
  setMemberRole, updateMyMemberProfile, getMyStats, listCommunities, joinCommunity, getActivity,
  listEvents, getEvent, createEvent, updateEvent, deleteEvent,
  registerForEvent, cancelEventRegistration, myEvents,
  listAnnouncements, pinnedAnnouncements, createAnnouncement, updateAnnouncement,
  toggleAnnouncementPin, deleteAnnouncement,
  listBadges, myBadges, communityBadges, awardBadge,
  listModules, updateModuleProgress, collectiveLearning,
  listCommunityCampaigns, getCommunityCampaign, getMyCampaigns, joinCommunityCampaign,
  shareCommunityCampaign, inviteToCommunityCampaign,
  createReport, listReports, resolveReport,
}
export default communityApi